// Miri embed shell.
//
// Lives at embed.miri.ai/<route>. Partners iframe this URL into their own
// web app to surface a Miri component (chat / today / progress / ...) on
// their page. The iframe URL carries:
//
//   #token=<token>                   — the token the SDK exchanges for a
//                                       Miri session. With provider=firebase
//                                       (default) this is a Firebase ID
//                                       token the partner's backend mints
//                                       for the care_seeker. With
//                                       provider=custom it is the partner's
//                                       own token, which Miri validates by
//                                       POSTing it to the partner's
//                                       token_validation_url webhook.
//                                       Carried in the URL *fragment*, not
//                                       the query string: fragments are
//                                       never sent to the server, so the
//                                       token never lands in access logs,
//                                       proxy logs, or referrer headers.
//   ?provider=<firebase|custom>      — optional auth provider. Defaults to
//                                       firebase. `custom` routes token
//                                       validation through the partner's
//                                       webhook configured on the api_key.
//   ?api_key=<override>              — optional override of the default
//                                       Miri tenant key.
//   ?module=<name>                   — optional moduleName for /chat.
//   ?env=<staging|production>        — optional miri-server env.
//   ?intro=<text>                    — optional coach first message.
//
// Theme params (see THEMING below):
//   ?bg=<hex>      — page / surface background color.
//   ?text=<hex>    — primary text color.
//   ?font=<name>   — font family (must be loaded in index.html for
//                    non-system fonts; Inter, Poppins, DM Sans bundled).
//   ?radius=<n>    — corner radius (px) for embed chrome cards/buttons.
//   ?padding=<n>   — route content padding (px, 0–64); overrides the
//                    per-route default so partners control spacing.
//   ?score=1       — (/progress) opt in to the Wellness Score card.
//   ?priority=1    — (/progress) opt in to the Today's Priority card.
//   ?insight=1     — (/progress) opt in to the Coach Insight card.
//
// THEMING — hybrid model:
//   • Background / text / fonts come from the URL params above and are
//     handed to the SDK via the `theme` prop on <MiriAppProvider>, which
//     themes every SDK component (Chat, ScoreCard, LeverBreakdown,
//     ScanFoodModal, ...). A dark `bg` auto-flips the SDK to dark mode.
//   • Accent / brand / CTA / chart colors are NOT URL params — they come
//     from the partner's practitioner record (brandColor / ctaButtonColor
//     / themeSeedColor). The SDK derives them server-side; the embed
//     chrome reads the same value off `useMiriApp().activeCoach` so the
//     chrome matches the SDK surfaces. Set a partner's brand color once
//     on their practitioner record and every iframe inherits it.
//   • `radius` only themes the embed's own chrome — SDK component radii
//     are fixed in the SDK design system.
//
// Route → surface:
//   /chat     — full-viewport Chat.
//   /today    — priority action + daily-plan insight + key signals.
//   /progress — sleep/hydration/fiber levers (tap a lever for its trend
//               detail + a log action) + weight progress chart + a link
//               to the logged-items list. Today's Priority + Coach
//               Insight are opt-in via ?priority / ?insight.
//   /coach    — chip-first view; pick a topic → scoped chat.
//   /log      — meal logging (ScanFoodModal: search / barcode / photo /
//               voice / chat) + structured logging for sleep, water & more
//               via the quick check-in flow + weight logging via the SDK
//               EditWeightSheet.
//
// Token lifecycle: read once from window.location.search on mount. The
// partner refreshes the token by swapping the iframe src before the 1h
// Firebase ID expiry.

import {
  Chat,
  ChatInput,
  DayView,
  EditWeightSheet,
  InsightCard,
  KeySignalsRow,
  LeverBreakdown,
  MessagesList,
  MiriAppProvider,
  PriorityActionCard,
  QuickCheckinFlow,
  ScoreCard,
  ScanFoodModal,
  TrendChart,
  VoiceCaptureProvider,
  VoiceMealFlow,
  useCoachChipsAPI,
  useInsights,
  useMiriApp,
  usePlanArtifactsAPI,
  usePriorityActionAPI,
  useProgressRepository,
  useQuickCheckinRepository,
  useWeightGoalsRepository,
  useWellnessScore,
  type BodyComposition,
  type LeverProgressItem,
  type PlanArtifactResponse,
  type PriorityActionResponse,
  type ProgressDashboardResponse,
  type ProgressDetailResponse,
  type QuickCheckinCardConfig,
  type QuickCheckinCardData,
} from '@miri-ai/miri-react-native';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PortalProvider } from '@gorhom/portal';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { useWebVoiceCapture } from './useWebVoiceCapture';
import { WeightProgressCard } from './WeightProgressCard';

// Default Miri tenant key. Partners with their own tenant override via
// `?api_key=`.
const DEFAULT_API_KEY =
  'miri-inc_alpha_ios_feb56312-42e9-4b4d-bd11-fa8244388378';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

type MiriEnv = 'staging' | 'alpha' | 'production';

const VALID_ENVS: MiriEnv[] = ['staging', 'alpha', 'production'];
const DEFAULT_ENV: MiriEnv = 'staging';

const FIREBASE_PROJECT_BY_ENV: Record<MiriEnv, string> = {
  staging: 'miri-staging',
  alpha: 'miri-staging',
  production: 'miri-production',
};

// Auth providers the hosted embed accepts. `firebase` is the default —
// the partner's backend mints a Firebase ID token (the demo-token POC
// path). `custom` routes validation through the partner's own
// token_validation_url webhook, configured server-side against the
// api_key; the embed just forwards the partner's token. `google` /
// `apple` aren't meaningful for an iframe surface and are not offered.
type EmbedProvider = 'firebase' | 'custom';

const VALID_PROVIDERS: EmbedProvider[] = ['firebase', 'custom'];
const DEFAULT_PROVIDER: EmbedProvider = 'firebase';

const DEFAULT_INTRO =
  "Hi! I've got your latest meds, weight, meals, sleep, and habits in front of me — " +
  'ask me about side effects, plateaus, dose timing, or anything else.';

// ---------------------------------------------------------------------------
// Theming
//
// `BaseTokens` are everything resolvable from the URL at parse time.
// `ThemeTokens` adds `accent` (+ derived `onAccent`), which is only known
// once the practitioner record has loaded inside the provider tree.
// ---------------------------------------------------------------------------

interface BaseTokens {
  bg: string; // page / surface background
  text: string; // primary text
  font: string; // font family
  radius: number; // corner radius for chrome
  pad: number | null; // route content padding override (null = per-route default)
  surface: string; // card background (derived)
  border: string; // hairline border (derived)
  textMuted: string; // secondary text (derived)
}

interface ThemeTokens extends BaseTokens {
  accent: string; // brand / CTA color (from practitioner record)
  onAccent: string; // readable text color on `accent`
}

const DEFAULT_ACCENT = '#1B1B16';

// `cursor:pointer` is required for RN-Web Pressables to register taps
// reliably in Mobile Safari — appended to every interactive style.
const pointer = { cursor: 'pointer' } as const;

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

// Blend color `a` toward color `b` by `t` (0..1). Used to derive surface /
// border / muted-text tokens so they adapt to any bg+text pair, including
// dark backgrounds.
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

// Pick black or white text for legibility on an arbitrary background.
function readableOn(bg: string): string {
  const [r, g, b] = hexToRgb(bg);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#1B1B16' : '#FFFFFF';
}

function normalizeHex(v: string | null, fallback: string): string {
  if (!v) return fallback;
  let h = v.trim();
  if (!h.startsWith('#')) h = `#${h}`;
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h) ? h : fallback;
}

function resolveBaseTokens(qs: URLSearchParams): BaseTokens {
  const bg = normalizeHex(qs.get('bg'), '#FFFFFF');
  const text = normalizeHex(qs.get('text'), '#1B1B16');
  const font = (qs.get('font') || '').trim() || 'Inter';
  const radiusRaw = qs.get('radius');
  const radiusNum = Number(radiusRaw);
  const radius =
    radiusRaw !== null && Number.isFinite(radiusNum)
      ? Math.max(0, Math.min(40, radiusNum))
      : 14;
  // `?padding=` overrides the route content padding so partners can
  // control spacing in their own layout. Absent → per-route defaults.
  const padRaw = qs.get('padding');
  const padNum = Number(padRaw);
  const pad =
    padRaw !== null && Number.isFinite(padNum)
      ? Math.max(0, Math.min(64, padNum))
      : null;
  return {
    bg,
    text,
    font,
    radius,
    pad,
    surface: mix(bg, text, 0.055),
    border: mix(bg, text, 0.14),
    textMuted: mix(text, bg, 0.42),
  };
}

function withAccent(base: BaseTokens, accent: string): ThemeTokens {
  return { ...base, accent, onAccent: readableOn(accent) };
}

// SDK `theme` prop — colors.background / colors.text drive the derived
// practitioner palette + dark-mode flip; fonts override every weight.
function buildSdkTheme(base: BaseTokens) {
  const f = (fontWeight: string) => ({ fontFamily: base.font, fontWeight });
  return {
    colors: { background: base.bg, text: base.text },
    fonts: {
      light: f('300'),
      regular: f('normal'),
      bold: f('500'),
      extraBold: f('600'),
      heavy: f('700'),
    },
  };
}

// ---------------------------------------------------------------------------
// Embed theme context — carries resolved tokens + the themed StyleSheet to
// every route component without prop drilling.
// ---------------------------------------------------------------------------

type EmbedStyles = ReturnType<typeof makeStyles>;

const EmbedThemeContext = createContext<{
  styles: EmbedStyles;
  t: ThemeTokens;
} | null>(null);

function useEmbedTheme() {
  const ctx = useContext(EmbedThemeContext);
  if (!ctx) throw new Error('useEmbedTheme must be used within EmbedThemeProvider');
  return ctx;
}

// Resolves `accent` from the practitioner record (hybrid model) and builds
// the themed StyleSheet. Must render inside <MiriAppProvider>.
const EmbedThemeProvider: FC<PropsWithChildren<{ base: BaseTokens }>> = ({
  base,
  children,
}) => {
  const { activeCoach } = useMiriApp();
  const accent =
    (activeCoach as { ctaButtonColor?: string; brandColor?: string } | null)
      ?.ctaButtonColor ||
    (activeCoach as { ctaButtonColor?: string; brandColor?: string } | null)
      ?.brandColor ||
    DEFAULT_ACCENT;
  const t = useMemo(() => withAccent(base, accent), [base, accent]);
  const styles = useMemo(() => makeStyles(t), [t]);
  return (
    <EmbedThemeContext.Provider value={{ styles, t }}>
      {children}
    </EmbedThemeContext.Provider>
  );
};

interface EmbedParams {
  token: string | null;
  provider: EmbedProvider;
  apiKey: string;
  moduleName: string;
  route: string;
  env: MiriEnv;
  introMessage: string;
  base: BaseTokens;
  showScore: boolean;
  showPriority: boolean;
  showInsight: boolean;
}

function parseParams(): EmbedParams {
  const qs = new URLSearchParams(window.location.search);
  // The token rides in the URL fragment (#token=...) so it never reaches
  // the server / CDN / proxy access logs. Everything else (theme params,
  // api_key, module, env) is a normal query param.
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  const route = path || 'chat';
  const envParam = qs.get('env') as MiriEnv | null;
  const env =
    envParam && VALID_ENVS.includes(envParam) ? envParam : DEFAULT_ENV;
  const providerParam = qs.get('provider') as EmbedProvider | null;
  const provider =
    providerParam && VALID_PROVIDERS.includes(providerParam)
      ? providerParam
      : DEFAULT_PROVIDER;
  const truthy = (v: string | null) => v === '1' || v === 'true';
  return {
    token: hash.get('token'),
    provider,
    apiKey: qs.get('api_key') || DEFAULT_API_KEY,
    moduleName: qs.get('module') || 'quickstart',
    route,
    env,
    introMessage: qs.get('intro') || DEFAULT_INTRO,
    base: resolveBaseTokens(qs),
    showScore: truthy(qs.get('score')),
    showPriority: truthy(qs.get('priority')),
    showInsight: truthy(qs.get('insight')),
  };
}

export const App: FC = () => {
  const [params, setParams] = useState<EmbedParams>(() => parseParams());

  useEffect(() => {
    const onPop = () => setParams(parseParams());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const { base } = params;

  // Paint the document background so there's no white flash behind the
  // RN-Web tree when a partner themes a non-white bg.
  useEffect(() => {
    document.body.style.background = base.bg;
    const root = document.getElementById('root');
    if (root) root.style.background = base.bg;
  }, [base.bg]);

  const sdkTheme = useMemo(() => buildSdkTheme(base), [base]);

  // Provider-specific auth config. `firebase` needs the project_id the
  // token was minted against; `custom` needs nothing — Miri resolves the
  // partner's token_validation_url server-side from the api_key.
  const auth = useMemo(
    () =>
      params.provider === 'custom'
        ? { token: params.token, provider: 'custom' as const, config: {} }
        : {
            token: params.token,
            provider: 'firebase' as const,
            config: { project_id: FIREBASE_PROJECT_BY_ENV[params.env] },
          },
    [params.provider, params.token, params.env],
  );

  if (!params.token) {
    const t = withAccent(base, DEFAULT_ACCENT);
    return (
      <EmbedThemeContext.Provider value={{ styles: makeStyles(t), t }}>
        <MissingToken />
      </EmbedThemeContext.Provider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SafeAreaProvider>
        <PortalProvider>
          <MiriAppProvider
            apiKey={params.apiKey}
            env={params.env}
            scheme="embed"
            userAgentPrefix="miri-embed/0.0.1"
            auth={auth}
            theme={sdkTheme as never}
            logError={(...args) => console.warn('[Miri]', ...args)}
          >
            <EmbedThemeProvider base={base}>
              {/* Supplies the SDK's VoiceMealFlow with a Web Speech API
                  STT hook — without a provider it falls back to
                  status='unsupported' and voice logging is dead. */}
              <VoiceCaptureProvider useVoiceCaptureImpl={useWebVoiceCapture}>
                <EmbedRoute params={params} />
              </VoiceCaptureProvider>
            </EmbedThemeProvider>
          </MiriAppProvider>
        </PortalProvider>
      </SafeAreaProvider>
    </QueryClientProvider>
  );
};

const EmbedRoute: FC<{ params: EmbedParams }> = ({ params }) => {
  switch (params.route) {
    case 'chat':
      return (
        <ChatRoute
          moduleName={params.moduleName}
          introMessage={params.introMessage}
        />
      );
    case 'today':
      return <TodayRoute />;
    case 'progress':
      return (
        <ProgressRoute
          showScore={params.showScore}
          showPriority={params.showPriority}
          showInsight={params.showInsight}
        />
      );
    case 'log':
      return <LogRoute />;
    case 'coach':
      return (
        <CoachRoute
          moduleName={params.moduleName}
          introMessage={params.introMessage}
        />
      );
    default:
      return <UnknownRoute route={params.route} />;
  }
};

// Shared header for drill-in / overlay views — back affordance + title.
const OverlayHeader: FC<{ title: string; onBack: () => void }> = ({
  title,
  onBack,
}) => {
  const { styles } = useEmbedTheme();
  return (
    <View style={styles.detailHeader}>
      <Pressable
        onPress={onBack}
        onClick={onBack}
        style={[styles.detailBackBtn, pointer]}
        accessibilityRole="button"
        accessibilityLabel="Back"
      >
        <Text style={styles.detailBackArrow}>‹</Text>
      </Pressable>
      <Text style={styles.detailTitle} numberOfLines={1}>
        {title}
      </Text>
    </View>
  );
};

// Today's priority action — fetch + render + mark-complete. Shared by
// /today and /progress.
const PriorityActionSection: FC = () => {
  const { getPriorityAction, completePriorityAction } = usePriorityActionAPI();
  const [priorityAction, setPriorityAction] =
    useState<PriorityActionResponse | null>(null);
  const [reinforcement, setReinforcement] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPriorityAction().then((pa) => {
      if (!cancelled) setPriorityAction(pa);
    });
    return () => {
      cancelled = true;
    };
  }, [getPriorityAction]);

  const handleMarkComplete = useCallback(async () => {
    if (!priorityAction || priorityAction.is_completed) return;
    setIsSubmitting(true);
    try {
      const result = await completePriorityAction();
      if (result) {
        setReinforcement(result.reinforcement_text);
        setPriorityAction({ ...priorityAction, is_completed: true });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [priorityAction, completePriorityAction]);

  if (!priorityAction) return null;
  return (
    <PriorityActionCard
      actionText={priorityAction.action_text}
      rationale={priorityAction.rationale}
      habitCategory={priorityAction.habit_category}
      isCompleted={priorityAction.is_completed}
      generatedAt={priorityAction.generated_at}
      reinforcementText={reinforcement}
      onMarkComplete={handleMarkComplete}
      isSubmitting={isSubmitting}
    />
  );
};

// Coach insight (daily-plan pattern analysis). Shared by /today and
// /progress.
const CoachInsightSection: FC = () => {
  const { data: insights } = useInsights();
  const dailyPlan = insights?.daily_plan;
  if (!dailyPlan || !insights) return null;
  return (
    <InsightCard
      subtitle="Daily plan · Pattern analysis"
      text={dailyPlan.text}
      status={insights.recomputation_status}
      lastUpdatedAt={dailyPlan.generated_at}
      awaitingCheckin={insights.awaiting_checkin}
    />
  );
};

// Weight progress — current weight, goal, and a weekly trend chart.
// Renders the WeightProgressCard ported from the multi-program SKU's
// Progress page; it self-hides when the user has no tracked weight.
const WeightSection: FC = () => {
  const { getWeightProgress } = useMiriApp();
  const [weight, setWeight] = useState<Awaited<
    ReturnType<typeof getWeightProgress>
  > | null>(null);

  useEffect(() => {
    let cancelled = false;
    getWeightProgress().then((w) => {
      if (!cancelled) setWeight(w);
    });
    return () => {
      cancelled = true;
    };
  }, [getWeightProgress]);

  if (!weight) return null;
  return (
    <WeightProgressCard
      weightTracking={weight.weightTracking}
      weightGoal={weight.weightGoal}
    />
  );
};

// Wellness score card — score + week-over-week change. Shared by /today
// and (opt-in) /progress.
const ScoreSection: FC = () => {
  const { styles } = useEmbedTheme();
  const { data: wellnessScore } = useWellnessScore();
  if (wellnessScore?.wellness_score == null) return null;
  const wsDelta = wellnessScore.wellness_score_delta;
  const change =
    typeof wsDelta === 'number' && wsDelta !== 0
      ? {
          value: Math.abs(Math.round(wsDelta)),
          direction: (wsDelta > 0 ? 'up' : 'down') as 'up' | 'down',
        }
      : undefined;
  // Cap the score card's width and center it — left unconstrained it
  // stretches uncomfortably wide on desktop-sized iframes.
  return (
    <View style={styles.scoreWrap}>
      <ScoreCard
        value={wellnessScore.wellness_score}
        label="WELLNESS SCORE"
        change={change}
      />
    </View>
  );
};

// /today — wellness score + KeySignalsRow + priority action + coach insight.
const TodayRoute: FC = () => {
  const { styles } = useEmbedTheme();
  const { data: wellnessScore } = useWellnessScore();
  const { getBodyComposition } = useMiriApp();
  const [bodyComposition, setBodyComposition] =
    useState<BodyComposition | null>(null);

  useEffect(() => {
    let cancelled = false;
    getBodyComposition().then((bc) => {
      if (!cancelled) setBodyComposition(bc);
    });
    return () => {
      cancelled = true;
    };
  }, [getBodyComposition]);

  return (
    <ScrollView style={styles.fill} contentContainerStyle={styles.routePadding}>
      <ScoreSection />
      <KeySignalsRow
        bodyComposition={bodyComposition}
        leverScores={wellnessScore?.lever_scores}
      />
      <PriorityActionSection />
      <CoachInsightSection />
    </ScrollView>
  );
};

// Quick check-in panel — structured logging for sleep, water, fiber & more.
// `getQuickCheckinCards` returns the cards relevant to the user right now;
// `QuickCheckinFlow` walks them; each completed card is persisted via
// `submitQuickCheckinCard`, and the flow finish triggers a plan recompute.
const QuickCheckinPanel: FC<{ onDone: () => void }> = ({ onDone }) => {
  const { styles } = useEmbedTheme();
  const { getQuickCheckinCards, submitQuickCheckinCard, completeQuickCheckinFlow } =
    useQuickCheckinRepository();
  const [cards, setCards] = useState<QuickCheckinCardConfig[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getQuickCheckinCards().then((cs) => {
      if (!cancelled) setCards(cs ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [getQuickCheckinCards]);

  if (!cards) {
    return <Text style={styles.panelMsg}>Loading your check-in…</Text>;
  }
  if (cards.length === 0) {
    return (
      <Text style={styles.panelMsg}>
        You're all caught up — nothing left to log right now.
      </Text>
    );
  }
  // The SDK's `sheet` presentation defaults to a bottom sheet that, inside
  // a fixed-height iframe, lands below the fold. Force `fullscreen` so the
  // field's input is fully visible. (Inline mode renders the card header
  // with no input field at all, so it's not an option.)
  const fixedCards = cards.map((c) =>
    c.presentation?.mode === 'sheet'
      ? {
          ...c,
          presentation: { ...c.presentation, sheetType: 'fullscreen' as const },
        }
      : c,
  );
  return (
    <QuickCheckinFlow
      cards={fixedCards}
      onCardComplete={(card: QuickCheckinCardConfig, data: QuickCheckinCardData) => {
        submitQuickCheckinCard({
          artifactType: card.artifactType,
          data,
          artifactId: card.existingArtifactId,
        });
      }}
      onFlowComplete={(
        _responses: Record<string, QuickCheckinCardData>,
        completed: QuickCheckinCardConfig[],
      ) => {
        completeQuickCheckinFlow(completed.map((c) => c.artifactType));
        onDone();
      }}
      onDismiss={onDone}
    />
  );
};

// /progress — today's priority action + coach insight + sleep/hydration/
// fiber levers. Tap a lever for its trend detail; tap the link for the
// logged-items history.
const PROGRESS_LEVER_IDS = new Set(['sleep', 'hydration', 'fiber']);

const ProgressRoute: FC<{
  showScore: boolean;
  showPriority: boolean;
  showInsight: boolean;
}> = ({ showScore, showPriority, showInsight }) => {
  const { styles } = useEmbedTheme();
  const { getProgressDashboard } = useProgressRepository();
  const [dashboard, setDashboard] = useState<ProgressDashboardResponse | null>(
    null,
  );
  const [detailLeverId, setDetailLeverId] = useState<string | null>(null);
  const [showLogged, setShowLogged] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getProgressDashboard().then((d) => {
      if (!cancelled) setDashboard(d);
    });
    return () => {
      cancelled = true;
    };
  }, [getProgressDashboard]);

  const levers = (dashboard?.levers ?? []).filter((l) =>
    PROGRESS_LEVER_IDS.has(l.leverId),
  );

  if (logOpen) {
    return (
      <View style={styles.fill}>
        <OverlayHeader title="Log" onBack={() => setLogOpen(false)} />
        <ScrollView contentContainerStyle={styles.routePadding}>
          <QuickCheckinPanel onDone={() => setLogOpen(false)} />
        </ScrollView>
      </View>
    );
  }
  if (detailLeverId) {
    return (
      <LeverDetailView
        leverId={detailLeverId}
        onBack={() => setDetailLeverId(null)}
        onLog={() => setLogOpen(true)}
      />
    );
  }
  if (showLogged) {
    return <LoggedItemsView onBack={() => setShowLogged(false)} />;
  }

  return (
    <ScrollView style={styles.fill} contentContainerStyle={styles.routePadding}>
      {showScore ? <ScoreSection /> : null}
      {showPriority ? <PriorityActionSection /> : null}
      {showInsight ? <CoachInsightSection /> : null}
      <WeightSection />
      {levers.length > 0 ? (
        <LeverBreakdown
          levers={levers}
          onLeverPress={(leverId: string) => setDetailLeverId(leverId)}
        />
      ) : null}
      <Pressable
        style={[styles.linkRow, pointer]}
        onPress={() => setShowLogged(true)}
        onClick={() => setShowLogged(true)}
        accessibilityRole="button"
      >
        <Text style={styles.linkLabel}>See everything you've logged →</Text>
      </Pressable>
    </ScrollView>
  );
};

// Lever detail — fetches the per-lever trend (today's value + weekly chart)
// via getProgressDetail, and offers a log action. `LeverBreakdown`'s
// onLeverPress hands back a `leverId` string, so the detail data is fetched
// here rather than passed in.
const LeverDetailView: FC<{
  leverId: string;
  onBack: () => void;
  onLog: () => void;
}> = ({ leverId, onBack, onLog }) => {
  const { styles } = useEmbedTheme();
  const { getProgressDetail } = useProgressRepository();
  const [detail, setDetail] = useState<ProgressDetailResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProgressDetail({ id: leverId }).then((d) => {
      if (!cancelled) setDetail(d);
    });
    return () => {
      cancelled = true;
    };
  }, [getProgressDetail, leverId]);

  const today = detail?.today;
  return (
    <View style={styles.fill}>
      <OverlayHeader title={today?.leverName ?? 'Progress'} onBack={onBack} />
      <ScrollView contentContainerStyle={styles.detailContent}>
        {today ? (
          <View style={styles.detailValueRow}>
            <Text style={styles.detailValueBig}>
              {today.value != null ? today.valueDisplay : '—'}
            </Text>
            <Text style={styles.detailGoalLabel}>
              / {today.goalDisplay} goal
            </Text>
          </View>
        ) : (
          <Text style={styles.panelMsg}>Loading…</Text>
        )}
        {detail?.trend ? (
          <TrendChart {...detail.trend} showBorder={false} />
        ) : null}
        <Pressable
          style={[styles.detailLogBtn, pointer]}
          onPress={onLog}
          onClick={onLog}
          accessibilityRole="button"
        >
          <Text style={styles.detailLogBtnLabel}>
            Log {today?.leverName ?? 'progress'}
          </Text>
        </Pressable>
      </ScrollView>
    </View>
  );
};

// Logged-items list — mirrors the RN app's log tab: a DayView of the
// artifacts logged on a given day. Reached via the "See everything you've
// logged" link on /progress.
//
// Scoped to the embed's nutrition surface (meals, water, sleep, bowel
// movements, symptoms) — movement and medication are out of MVP scope.
const LOG_ARTIFACT_TYPES = [
  'meal_tracking_item',
  'water_intake_tracking',
  'tracking_item_sleep',
  'tracking_item_bowel_movements',
  'symptom_tracking',
];

// Day key for an artifact — server recorded_date wins, then the artifact's
// own data.date, falling back to the created_at calendar day.
function artifactDay(a: PlanArtifactResponse): string {
  const withDate = a as { recorded_date?: string; data?: { date?: string } };
  return (
    withDate.recorded_date ??
    withDate.data?.date ??
    new Date(a.created_at).toISOString().slice(0, 10)
  );
}

const LoggedItemsView: FC<{ onBack: () => void }> = ({ onBack }) => {
  const { styles } = useEmbedTheme();
  const { getPlanArtifactsByTypes } = usePlanArtifactsAPI();
  const [artifacts, setArtifacts] = useState<PlanArtifactResponse[] | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    // No created-at floor: demo artifacts are backfilled, so `created_at`
    // can be far older than the day they're recorded for. Pull a generous
    // page and group by the recorded day below.
    getPlanArtifactsByTypes({
      artifactTypes: LOG_ARTIFACT_TYPES,
      limit: 200,
    })
      .then((r) => {
        if (!cancelled) setArtifacts(r?.plan_artifacts ?? []);
      })
      .catch(() => {
        if (!cancelled) setArtifacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [getPlanArtifactsByTypes]);

  // Show the most recent day that actually has entries, so the view isn't
  // empty when nothing was logged today.
  const days = artifacts
    ? [...new Set(artifacts.map(artifactDay))].sort().reverse()
    : [];
  const activeDay = days[0];
  const dayArtifacts =
    artifacts?.filter((a) => artifactDay(a) === activeDay) ?? [];

  return (
    <View style={styles.fill}>
      <OverlayHeader title="Logged items" onBack={onBack} />
      {artifacts == null ? (
        <Text style={styles.panelMsg}>Loading your log…</Text>
      ) : dayArtifacts.length === 0 ? (
        <Text style={styles.panelMsg}>Nothing logged yet.</Text>
      ) : (
        <ScrollView contentContainerStyle={styles.routePadding}>
          <DayView
            date={new Date(activeDay)}
            artifacts={dayArtifacts}
            showDateHeader
            // With onMealPress set, DayView delegates the tap instead of
            // opening its PlanArtifactFormSheet — that sheet fails to load
            // on web ("Error loading form"). No-op keeps the log read-only.
            onMealPress={() => {}}
          />
        </ScrollView>
      )}
    </View>
  );
};

// Weight logging — wraps the SDK's EditWeightSheet. The sheet persists the
// new weight itself (it calls updateWeightTracking internally); we just
// feed it the current weight + goal and refresh after a save.
const WeightLogSheet: FC<{ visible: boolean; onClose: () => void }> = ({
  visible,
  onClose,
}) => {
  const { getWeightGoals, getWeightTracking } = useWeightGoalsRepository();
  const [weightGoal, setWeightGoal] = useState<
    Awaited<ReturnType<typeof getWeightGoals>>
  >(null);
  const [weightTracking, setWeightTracking] = useState<Awaited<
    ReturnType<typeof getWeightTracking>
  > | null>(null);

  useEffect(() => {
    let cancelled = false;
    getWeightGoals().then((g) => {
      if (!cancelled) setWeightGoal(g);
    });
    getWeightTracking().then((t) => {
      if (!cancelled) setWeightTracking(t);
    });
    return () => {
      cancelled = true;
    };
  }, [getWeightGoals, getWeightTracking]);

  return (
    <EditWeightSheet
      visible={visible}
      weightTracking={weightTracking}
      weightGoal={weightGoal}
      onDismiss={onClose}
      onUpdateWeightTracking={async () => {
        const t = await getWeightTracking();
        setWeightTracking(t);
      }}
    />
  );
};

// /log — logging hub. Three entry cards:
//   • Log a meal → ScanFoodModal (search / barcode / photo / voice / chat).
//     Voice is wired via renderVoiceCapture→VoiceMealFlow; Chat via
//     onChatTap, which swaps in a conversational logging surface.
//   • Log sleep, water & more → the quick check-in flow.
//   • Log your weight → the SDK EditWeightSheet.
const LogRoute: FC = () => {
  const { styles } = useEmbedTheme();
  const [mealOpen, setMealOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [weightOpen, setWeightOpen] = useState(false);
  const [loggedCount, setLoggedCount] = useState(0);

  // The web CameraPermissionGate shim (shown when a visitor taps
  // Photo/Barcode, which can't work on web) has no prop access to this
  // component, so its close button bridges out via a window event.
  // Listen for it here and dismiss the whole ScanFoodModal.
  useEffect(() => {
    const close = () => setMealOpen(false);
    window.addEventListener('miri-close-meal-hub', close);
    return () => window.removeEventListener('miri-close-meal-hub', close);
  }, []);

  // Conversational meal logging — opened from the modal's Chat tile.
  if (chatOpen) {
    return (
      <View style={styles.fill}>
        <OverlayHeader title="Log with Miri" onBack={() => setChatOpen(false)} />
        <View style={styles.chatRoot}>
          <Chat
            moduleName="log_meal_v3"
            introMessage="Tell me what you ate and I'll log it — or ask me anything about your nutrition."
          >
            <MessagesList />
            <ChatInput />
          </Chat>
        </View>
      </View>
    );
  }

  // Structured logging — sleep, water, fiber & more.
  if (qcOpen) {
    return (
      <View style={styles.fill}>
        <OverlayHeader
          title="Log sleep, water & more"
          onBack={() => setQcOpen(false)}
        />
        <ScrollView contentContainerStyle={styles.routePadding}>
          <QuickCheckinPanel onDone={() => setQcOpen(false)} />
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.fill}>
      <ScrollView contentContainerStyle={styles.logEntryContent}>
        <Text style={styles.logEyebrow}>Powered by Miri</Text>
        <Text style={styles.logTitle}>Log</Text>

        {loggedCount > 0 ? (
          <View style={styles.logSuccessBanner}>
            <Text style={styles.logSuccessText}>
              ✓ {loggedCount} meal{loggedCount > 1 ? 's' : ''} logged this
              session
            </Text>
          </View>
        ) : null}

        <Pressable
          style={[styles.logMealCard, pointer]}
          onPress={() => setMealOpen(true)}
          onClick={() => setMealOpen(true)}
          accessibilityRole="button"
        >
          <View style={styles.logMealCardIcon}>
            <Text style={styles.logMealCardIconLabel}>🍽</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.logMealCardTitle}>
              {loggedCount > 0 ? 'Log another meal' : 'Log a meal'}
            </Text>
            <Text style={styles.logMealCardBody}>
              Search, scan a barcode, snap a photo, use voice or chat
            </Text>
          </View>
          <Text style={styles.logMealCardChevron}>›</Text>
        </Pressable>

        <Pressable
          style={[styles.logMealCard, pointer]}
          onPress={() => setQcOpen(true)}
          onClick={() => setQcOpen(true)}
          accessibilityRole="button"
        >
          <View style={styles.logMealCardIcon}>
            <Text style={styles.logMealCardIconLabel}>💧</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.logMealCardTitle}>Log sleep, water & more</Text>
            <Text style={styles.logMealCardBody}>
              A quick structured check-in for sleep, hydration, fiber and
              other daily signals
            </Text>
          </View>
          <Text style={styles.logMealCardChevron}>›</Text>
        </Pressable>

        <Pressable
          style={[styles.logMealCard, pointer]}
          onPress={() => setWeightOpen(true)}
          onClick={() => setWeightOpen(true)}
          accessibilityRole="button"
        >
          <View style={styles.logMealCardIcon}>
            <Text style={styles.logMealCardIconLabel}>⚖️</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.logMealCardTitle}>Log your weight</Text>
            <Text style={styles.logMealCardBody}>
              Record today's weight and track progress toward your goal
            </Text>
          </View>
          <Text style={styles.logMealCardChevron}>›</Text>
        </Pressable>
      </ScrollView>

      <ScanFoodModal
        visible={mealOpen}
        onClose={() => setMealOpen(false)}
        initialMode="search"
        onLogMeal={() => {
          setLoggedCount((c) => c + 1);
          setMealOpen(false);
        }}
        onChatTap={() => {
          setMealOpen(false);
          setChatOpen(true);
        }}
        renderVoiceCapture={({ closeVoiceView }) => (
          <VoiceMealFlow
            onClose={closeVoiceView}
            onMealSaved={() => setLoggedCount((c) => c + 1)}
          />
        )}
      />

      <WeightLogSheet
        visible={weightOpen}
        onClose={() => setWeightOpen(false)}
      />
    </View>
  );
};

// /chat — full-viewport Chat surface scoped to `moduleName`.
type ChatRouteProps = { moduleName: string; introMessage: string };

const ChatRoute: FC<ChatRouteProps> = ({ moduleName, introMessage }) => {
  const { styles } = useEmbedTheme();
  return (
    <View style={styles.chatRoot}>
      <Chat moduleName={moduleName} introMessage={introMessage}>
        <MessagesList />
        <ChatInput />
      </Chat>
    </View>
  );
};

// /coach — chip-first view. Tapping a chip swaps in a chat surface scoped
// to that chip's module + seeds it with the chip's prompt_text.
type CoachChip = {
  chip_id: string;
  label: string;
  module_name: string;
  prompt_text: string;
  domain?: string;
};

const CoachRoute: FC<ChatRouteProps> = ({ moduleName, introMessage }) => {
  const { styles } = useEmbedTheme();
  const [activeChip, setActiveChip] = useState<CoachChip | null>(null);

  if (activeChip) {
    const hasPrompt = !!activeChip.prompt_text;
    return (
      <View style={styles.chatRoot}>
        <View style={styles.coachChatHeader}>
          <Pressable
            onPress={() => setActiveChip(null)}
            onClick={() => setActiveChip(null)}
            style={[styles.coachBackBtn, pointer]}
            accessibilityRole="button"
            accessibilityLabel="Back to topics"
          >
            <Text style={styles.coachBackArrow}>‹</Text>
          </Pressable>
          <Text style={styles.coachChatTitle} numberOfLines={1}>
            {activeChip.label}
          </Text>
        </View>
        <View style={styles.chatRoot}>
          <Chat
            key={activeChip.chip_id}
            moduleName={activeChip.module_name}
            introMessage={hasPrompt ? undefined : introMessage}
            sendUserMessage={hasPrompt ? activeChip.prompt_text : undefined}
          >
            <MessagesList />
            <ChatInput />
          </Chat>
        </View>
      </View>
    );
  }

  return (
    <CoachChipGrid
      moduleName={moduleName}
      introMessage={introMessage}
      onChipPress={setActiveChip}
    />
  );
};

// Full-viewport chip grid. `useCoachChipsAPI` returns chips already ranked
// server-side by relevance to the care_seeker's own data.
const CoachChipGrid: FC<{
  moduleName: string;
  introMessage: string;
  onChipPress: (chip: CoachChip) => void;
}> = ({ moduleName, introMessage, onChipPress }) => {
  const { styles } = useEmbedTheme();
  const { getCoachChips } = useCoachChipsAPI();
  const [chips, setChips] = useState<CoachChip[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getCoachChips().then((resp) => {
      if (cancelled) return;
      setChips((resp?.chips ?? []) as CoachChip[]);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [getCoachChips]);

  return (
    <ScrollView
      style={styles.chatRoot}
      contentContainerStyle={styles.coachGridContent}
    >
      <Text style={styles.coachGridEyebrow}>Talk to your AI coach</Text>
      <Text style={styles.coachGridTitle}>What's on your mind?</Text>
      <Text style={styles.coachGridSubtitle}>{introMessage}</Text>

      {isLoading && chips.length === 0 ? (
        <Text style={styles.coachGridLoading}>Loading topics…</Text>
      ) : null}

      <View style={styles.coachChipGrid}>
        {chips.map((chip) => (
          <Pressable
            key={chip.chip_id}
            style={[styles.coachChipCard, pointer]}
            onPress={() => onChipPress(chip)}
            onClick={() => onChipPress(chip)}
            accessibilityRole="button"
          >
            <Text style={styles.coachChipCardLabel}>{chip.label}</Text>
          </Pressable>
        ))}
      </View>

      <Pressable
        style={[styles.coachAskAnything, pointer]}
        onPress={() =>
          onChipPress({
            chip_id: '__ask_anything__',
            label: 'Ask Miri anything',
            module_name: moduleName,
            prompt_text: '',
          })
        }
        onClick={() =>
          onChipPress({
            chip_id: '__ask_anything__',
            label: 'Ask Miri anything',
            module_name: moduleName,
            prompt_text: '',
          })
        }
      >
        <Text style={styles.coachAskAnythingLabel}>💬 Ask Miri anything →</Text>
      </Pressable>
    </ScrollView>
  );
};

const MissingToken: FC = () => {
  const { styles } = useEmbedTheme();
  return (
    <View style={styles.errorPanel}>
      <Text style={styles.errorTitle}>Missing token</Text>
      <Text style={styles.errorBody}>
        This iframe URL needs a <Text style={styles.mono}>?token=</Text> query
        parameter. Your backend should mint a Firebase ID token for the
        care_seeker and embed it in the iframe src.
      </Text>
    </View>
  );
};

const UnknownRoute: FC<{ route: string }> = ({ route }) => {
  const { styles } = useEmbedTheme();
  return (
    <View style={styles.errorPanel}>
      <Text style={styles.errorTitle}>Unknown route</Text>
      <Text style={styles.errorBody}>
        <Text style={styles.mono}>/{route}</Text> isn't a route the Miri embed
        currently serves. Supported routes:{' '}
        <Text style={styles.mono}>/chat</Text>,{' '}
        <Text style={styles.mono}>/today</Text>,{' '}
        <Text style={styles.mono}>/progress</Text>,{' '}
        <Text style={styles.mono}>/coach</Text>,{' '}
        <Text style={styles.mono}>/log</Text>.
      </Text>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Themed StyleSheet factory. Called once per resolved token set; every
// color / radius / font value below is a function of `t`.
// ---------------------------------------------------------------------------

function makeStyles(t: ThemeTokens) {
  const r = t.radius;
  const rSm = Math.round(r * 0.7);
  return StyleSheet.create({
    fill: { flex: 1, width: '100%', height: '100%', backgroundColor: t.bg },
    routePadding: { padding: t.pad ?? 16, gap: 12 },
    chatRoot: { flex: 1, backgroundColor: t.bg },
    // Score card width cap — keeps it from stretching on wide iframes.
    scoreWrap: { width: '100%', maxWidth: 480, alignSelf: 'center' },

    // Coach view (chip-first)
    coachGridContent: { padding: t.pad ?? 20, gap: 12 },
    coachGridEyebrow: {
      fontSize: 11,
      fontFamily: t.font,
      fontWeight: '600',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: t.textMuted,
    },
    coachGridTitle: {
      fontSize: 22,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.text,
      marginBottom: 4,
    },
    coachGridSubtitle: {
      fontSize: 14,
      fontFamily: t.font,
      lineHeight: 20,
      color: t.textMuted,
      marginBottom: 12,
    },
    coachGridLoading: {
      fontSize: 13,
      fontFamily: t.font,
      color: t.textMuted,
      paddingVertical: 16,
    },
    coachChipGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    coachChipCard: {
      flexGrow: 1,
      flexBasis: '47%',
      paddingHorizontal: 14,
      paddingVertical: 14,
      borderRadius: r,
      backgroundColor: t.surface,
      borderWidth: 1,
      borderColor: t.border,
    },
    coachChipCardLabel: {
      fontSize: 14,
      fontFamily: t.font,
      fontWeight: '600',
      color: t.text,
      lineHeight: 18,
    },
    coachAskAnything: {
      marginTop: 16,
      paddingVertical: 14,
      paddingHorizontal: 16,
      borderRadius: r,
      backgroundColor: t.accent,
      alignItems: 'center',
    },
    coachAskAnythingLabel: {
      fontSize: 14,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.onAccent,
    },
    coachChatHeader: {
      flexGrow: 0,
      flexShrink: 0,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: t.border,
      backgroundColor: t.surface,
      gap: 8,
    },
    coachBackBtn: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.bg,
      borderWidth: 1,
      borderColor: t.border,
    },
    coachBackArrow: {
      fontSize: 20,
      fontFamily: t.font,
      fontWeight: '600',
      color: t.text,
      marginTop: -2,
    },
    coachChatTitle: {
      flex: 1,
      fontSize: 14,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.text,
    },

    errorPanel: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      gap: 8,
      backgroundColor: t.bg,
    },
    errorTitle: {
      fontSize: 16,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.text,
    },
    errorBody: {
      fontSize: 13,
      fontFamily: t.font,
      color: t.textMuted,
      textAlign: 'center',
      lineHeight: 18,
      maxWidth: 480,
    },
    mono: { fontFamily: 'Menlo,Monaco,monospace' },

    // /progress lever detail
    detailHeader: {
      flexGrow: 0,
      flexShrink: 0,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: t.border,
      backgroundColor: t.surface,
      gap: 8,
    },
    detailBackBtn: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.bg,
      borderWidth: 1,
      borderColor: t.border,
    },
    detailBackArrow: {
      fontSize: 20,
      fontFamily: t.font,
      fontWeight: '600',
      color: t.text,
      marginTop: -2,
    },
    detailTitle: {
      flex: 1,
      fontSize: 14,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.text,
    },
    detailContent: { padding: t.pad ?? 20, gap: 12 },
    detailValueRow: { flexDirection: 'row', alignItems: 'baseline', gap: 6 },
    detailValueBig: {
      fontSize: 40,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.text,
    },
    detailGoalLabel: { fontSize: 16, fontFamily: t.font, color: t.textMuted },

    // /log route
    logEntryContent: { padding: t.pad ?? 20, gap: 16 },
    logEyebrow: {
      fontSize: 11,
      fontFamily: t.font,
      fontWeight: '600',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: t.textMuted,
    },
    logTitle: {
      fontSize: 22,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.text,
    },
    logSuccessBanner: {
      padding: 12,
      borderRadius: rSm,
      backgroundColor: '#EBF5EE',
      borderWidth: 1,
      borderColor: '#4A9D5F',
    },
    logSuccessText: {
      fontSize: 13,
      fontFamily: t.font,
      fontWeight: '600',
      color: '#4A9D5F',
    },
    logMealCard: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      borderRadius: r,
      backgroundColor: t.surface,
      borderWidth: 1,
      borderColor: t.border,
      gap: 14,
    },
    logMealCardIcon: {
      width: 44,
      height: 44,
      borderRadius: rSm,
      backgroundColor: t.bg,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: t.border,
    },
    logMealCardIconLabel: { fontSize: 22 },
    logMealCardTitle: {
      fontSize: 15,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.text,
      marginBottom: 3,
    },
    logMealCardBody: {
      fontSize: 12,
      fontFamily: t.font,
      color: t.textMuted,
      lineHeight: 17,
    },
    logMealCardChevron: {
      fontSize: 22,
      fontFamily: t.font,
      color: t.textMuted,
      fontWeight: '300',
    },

    // Shared: overlay panels, links, log action
    panelMsg: {
      fontSize: 14,
      fontFamily: t.font,
      color: t.textMuted,
      textAlign: 'center',
      paddingVertical: 24,
    },
    linkRow: { paddingVertical: 14, paddingHorizontal: 4 },
    linkLabel: {
      fontSize: 14,
      fontFamily: t.font,
      fontWeight: '600',
      color: t.accent,
    },
    detailLogBtn: {
      marginTop: 8,
      paddingVertical: 14,
      paddingHorizontal: 16,
      borderRadius: r,
      backgroundColor: t.accent,
      alignItems: 'center',
    },
    detailLogBtnLabel: {
      fontSize: 14,
      fontFamily: t.font,
      fontWeight: '700',
      color: t.onAccent,
    },
  });
}
